import i18next from 'i18next';
import * as configJson from '../config.json';
import { GetSegmentKeyFunction, SegmentKey, KEYS } from './text/keys';

export const config = configJson;
export type { TextTranslators, NoParamsKeys, TranslatableTextNoParams, TranslatableTextWithParams } from './text/text';
export type { GetSegmentKeyFunction, SegmentKey, KEYS } from './text/keys';
export type { TranslatableText } from './text/text';
export { makeTextService } from './text/text';

let i18NextInstance = i18next;

/**
 * We have some issues when i18next is included from @deltasierra and is linked.
 * Call this before loading i18next to force the translate function to use the correct
 * instance.
 *
 * @param newI18NextInstance - The correct i18next instance
 */
export function setI18NextInstance(newI18NextInstance: i18next.i18n): void {
    i18NextInstance = newI18NextInstance;
}

export type TranslateOptions = {
    [key: string]: any; // TODO: work out a better way of supporting different options when calling translate().
} & {
    localeCode?: string;
    context?: any;
    count?: number;
    interpolation?: i18next.InterpolationOptions;
};

export interface TranslateFunction {
    // eslint-disable-next-line @typescript-eslint/prefer-function-type
    (key: GetSegmentKeyFunction | SegmentKey, options?: TranslateOptions): string;
}

/**
 * This is the translation function that should be used in
 *
 * @example
 * console.log(translate('COMMON.CANCEL')); // Output depends on which language is detected by i18next
 * console.log(translation('COMMON.WELCOME_MESSAGE', { name: 'Frank Smith' });
 * @param key - The translation key
 * @param options - Any addition options to
 * @returns The translated string
 */
export const translate: TranslateFunction = (key, options?): string => {
    let keyName: string = typeof key === 'function' ? key(KEYS) : key;

    let actualOptions: any;

    if (options) {
        if ((options as any).values && typeof options === 'object') {
            actualOptions = { ...options, ...(options as any).values };
            delete actualOptions.values;
        } else {
            actualOptions = { ...options };
        }

        if (actualOptions.lng) {
            delete actualOptions.lng;
        }
    } else {
        actualOptions = {};
    }

    actualOptions.interpolation ||= {};
    actualOptions.interpolation.escapeValue ||= false;

    if (actualOptions.localeCode) {
        actualOptions.lng = actualOptions.localeCode;
    }

    // eslint-disable-next-line prefer-const
    let [namespace, ...keyParts] = keyName.split('.');
    namespace = camelCase(namespace);
    keyName = `${namespace}.${keyParts.join('.')}`;

    let result: string = i18NextInstance.t(keyName, actualOptions) || '';

    /*
     * Replace any occurrence of text within backticks with just the text minus the backticks as this are how we encode
     * literal text within translations
     */
    result = result.replace(/`(.*?)`/gi, (_: string, literal: string) => literal);

    return result;
};

function camelCase(text: string) {
    let result = text
        .replace(/_+/gi, ' ')
        .replace(/\w+\s*/g, (word: string) => word.charAt(0).toUpperCase() + word.substr(1).toLowerCase())
        .replace(/(\s|_)+/gi, '');
    result = result.charAt(0).toLowerCase() + result.substr(1);
    return result;
}
